import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EExamKey } from '@gray/domain/enums/exam-key.enum';
import { ILevel } from '@gray/domain/models/user/level.model';
import { IExam } from '@gray/domain/models/exam/exam.model';
import { IPeriod } from '@gray/domain/models/exam/exam-period.model';
import { IGrade } from '@gray/domain/models/lesson/grade.model';
import { IDepartment } from '@gray/domain/models/lesson/department.model';
import { examShortName } from '@gray/domain/helpers/exam.helper';
import { CoachStudentService } from '../../core/services/coach-student.service';
import { CoachCatalogService } from '../../core/services/coach-catalog.service';
import { CoachPeriodService } from '../../core/services/coach-period.service';
import {
  ICoachStudentsSummary,
  IStudentExamOverview,
  IStudentOverview,
} from '../../core/models/student-overview.model';

const PAGE_SIZE = 12;

/** Bootstrap's xl breakpoint: below it the detail panel is stacked under the roster. */
const XL_BREAKPOINT = 1200;

/** A level as the "Renk" select shows it: the name carries the score range of the active exam. */
interface ILevelOption {
  id: number;
  name: string;
}

@Component({
  selector: 'coach-students',
  templateUrl: './students.component.html',
  styleUrl: './students.component.scss',
})
export class StudentsComponent implements OnInit, OnDestroy {
  /**
   * Route parameter of `students/:id`. The roster and the detail are one screen, so opening a
   * student only swaps the panel on the right — the id stays in the URL to keep it linkable.
   */
  @Input() set id(value: string) {
    const parsed = Number(value);
    this.selectedStudentId = Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  @ViewChild('detailPanel') detailPanel?: ElementRef<HTMLElement>;

  selectedStudentId: number | null = null;

  summary?: ICoachStudentsSummary;
  students: IStudentOverview[] = [];

  page = 1;
  lastPage = 1;
  total = 0;

  /** Which exam the colours, the level badge and the level filter follow. */
  examKey = EExamKey.TYT;
  examKeys = EExamKey;

  periods: IPeriod[] = [];
  levels: ILevel[] = [];
  grades: IGrade[] = [];
  departments: IDepartment[] = [];
  exams: IExam[] = [];

  levelOptions: ILevelOption[] = [];

  fg: FormGroup = this.formBuilder.group({
    search: [null],
    periodId: [null, [Validators.required]],
    gradeId: [null],
    departmentId: [null],
    levelId: [null],
  });

  /**
   * True from the start: the roster is only asked for once the period and the catalogs are in, and
   * until then the screen must show the list loading rather than "no students".
   */
  isLoading = true;
  catalogIsLoading = false;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private studentService: CoachStudentService,
    private catalogService: CoachCatalogService,
    private periodService: CoachPeriodService,
    private translate: TranslateService,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.getCatalogs();
    this.watchFilters();
  }

  // region Catalogs
  /**
   * The period has to be resolved before anything else: levels, grades and scores are all stored
   * per period, so the roster cannot be asked for until one is picked.
   */
  getCatalogs() {
    this.catalogIsLoading = true;
    this.periodService.loadPeriods()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.periods = this.periodService.periods;
        this.fg.patchValue({periodId: this.periodService.selectedId}, {emitEvent: false});
        this.getPeriodCatalogs();
      });
  }

  /** Level ranges and grade labels differ per period, so they follow the selected one. */
  getPeriodCatalogs() {
    const periodId = this.fg.value.periodId;

    this.catalogIsLoading = true;
    forkJoin({
      levels: this.catalogService.getLevels(),
      grades: this.catalogService.getGrades(periodId),
      departments: this.catalogService.getDepartments(),
      exams: this.catalogService.getExams(),
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.catalogIsLoading = false))
      .subscribe(r => {
        this.levels = (r.levels.data?.result ?? r.levels.data ?? []) as ILevel[];
        this.grades = (r.grades.data?.result ?? r.grades.data ?? []) as IGrade[];
        this.departments = (r.departments.data?.result ?? r.departments.data ?? []) as IDepartment[];
        this.exams = (r.exams.data?.result ?? r.exams.data ?? []) as IExam[];

        this.buildLevelOptions();
        this.getStudents();
        this.getSummary();
      });
  }

  /** "Mavi (60 – 79 net)" — the range belongs to the exam that is currently selected. */
  buildLevelOptions() {
    const examId = this.activeExamId();

    this.levelOptions = this.levels.map(level => {
      const name = this.translate.instant(`levels.${level.key}`);
      const range = level.examScores?.find(score => score.examId === examId);
      if (!range || (range.minScore === null && range.maxScore === null)) {
        return {id: level.id, name};
      }

      const label = this.translate.instant('students.level_range', {
        min: range.minScore ?? 0,
        max: range.maxScore ?? '+',
      });
      return {id: level.id, name: `${name} (${label})`};
    });
  }
  // endregion

  // region Data
  getStudents() {
    if (!this.fg.value.periodId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.studentService.getStudentsOverview(this.buildQueryString())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.students = (r.data?.result ?? []) as IStudentOverview[];
        this.total = r.data?.meta?.total ?? 0;
        this.lastPage = r.data?.meta?.lastPage ?? 1;

        // The detail panel is never empty while there is someone to show — but a list that just
        // reloaded must not drag the page down to it.
        const isVisible = this.students.some(student => student.id === this.selectedStudentId);
        if (!isVisible && this.students.length) {
          this.openStudent(this.students[0].id, false);
        }
      });
  }

  getSummary() {
    if (!this.fg.value.periodId) {
      return;
    }

    this.studentService.getStudentsSummary(`?periodId=${this.fg.value.periodId}&examKey=${this.examKey}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.summary = r.data as ICoachStudentsSummary;
      });
  }

  /** Typing narrows the list on its own; the other filters apply as soon as they change. */
  watchFilters() {
    this.fg.get('search').valueChanges
      .pipe(takeUntil(this.destroyed$), debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.applyFilters());

    ['gradeId', 'departmentId', 'levelId'].forEach(control => {
      this.fg.get(control).valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.applyFilters());
    });

    this.fg.get('periodId').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(periodId => {
        this.periodService.select(periodId);
        this.page = 1;
        // A new period means new levels, new grades and new scores.
        this.fg.patchValue({gradeId: null, levelId: null}, {emitEvent: false});
        this.getPeriodCatalogs();
      });
  }

  applyFilters() {
    this.page = 1;
    this.getStudents();
  }

  setExamKey(examKey: EExamKey) {
    if (this.examKey === examKey) {
      return;
    }

    this.examKey = examKey;
    this.buildLevelOptions();
    this.applyFilters();
    this.getSummary();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.lastPage || page === this.page) {
      return;
    }

    this.page = page;
    this.getStudents();
  }
  // endregion

  // region View helpers
  activeExamId(): number | null {
    return this.exams.find(exam => exam.examKey === this.examKey)?.id ?? null;
  }

  /** Short form: the filter switch and the score boxes have no room for the full exam name. */
  examName(examKey: EExamKey): string {
    return examShortName(this.exams.find(exam => exam.examKey === examKey), examKey);
  }

  /** The exam side of a student the screen is currently reading. */
  examOf(student: IStudentOverview): IStudentExamOverview {
    return this.examKey === EExamKey.AYT ? student.proficiency : student.base;
  }

  levelKey(student: IStudentOverview): string {
    return this.examOf(student).level?.key ?? 'default';
  }

  levelName(student: IStudentOverview): string | null {
    const key = this.examOf(student).level?.key;
    return key ? this.translate.instant(`levels.${key}`) : null;
  }

  /**
   * How far the student is along the whole scale of the selected exam — the same number the detail
   * header shows as "Genel Durum". The scale spans from the lowest level's floor to the highest
   * level's ceiling, both read from the per-exam ranges.
   */
  overallPercent(student: IStudentOverview): number {
    const score = this.examOf(student).score;
    const examId = this.activeExamId();
    if (score === null || score === undefined || !examId) {
      return 0;
    }

    const ranges = this.levels
      .map(level => level.examScores?.find(examScore => examScore.examId === examId))
      .filter(range => !!range);
    if (!ranges.length) {
      return 0;
    }

    const min = Math.min(...ranges.map(range => range.minScore ?? 0));
    const max = Math.max(...ranges.map(range => range.maxScore ?? range.minScore ?? 0));
    if (max <= min) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round(((score - min) / (max - min)) * 100)));
  }

  metaLine(student: IStudentOverview): string {
    return [student.grade?.name, student.period?.name, student.department?.name]
      .filter(part => !!part)
      .join(' · ');
  }


  pageNumbers(): number[] {
    // A short window around the current page keeps the pager readable on a phone.
    const start = Math.max(1, Math.min(this.page - 2, this.lastPage - 4));
    const end = Math.min(this.lastPage, start + 4);

    const pages: number[] = [];
    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  /**
   * Selecting a student swaps the panel on the right; the URL follows so the view is linkable.
   * Below xl the panel sits under the roster, so the coach is taken to it.
   */
  openStudent(studentId: number, scrollToDetail = true) {
    this.selectedStudentId = studentId;
    this.router.navigate(['/dashboard/students', studentId], {replaceUrl: true});

    if (scrollToDetail) {
      this.scrollToDetail();
    }
  }

  private scrollToDetail() {
    // On xl and up the roster and the detail are side by side, so there is nothing to scroll to.
    if (window.innerWidth >= XL_BREAKPOINT) {
      return;
    }

    // Waits for the panel to be rendered with the newly selected student.
    setTimeout(() => this.detailPanel?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'}));
  }
  // endregion

  private buildQueryString(): string {
    const value = this.fg.value;
    const parts = [
      `page=${this.page}`,
      `size=${PAGE_SIZE}`,
      `periodId=${value.periodId}`,
      `examKey=${this.examKey}`,
    ];

    if (value.search?.trim()) {
      parts.push(`search=${encodeURIComponent(value.search.trim())}`);
    }
    if (value.gradeId) {
      parts.push(`filter[gradeId]=${value.gradeId}`);
    }
    if (value.departmentId) {
      parts.push(`filter[departmentId]=${value.departmentId}`);
    }
    if (value.levelId) {
      parts.push(`filter[levelId]=${value.levelId}`);
    }

    // TODO: COACH! Three more parameters belong here once the API supports them:
    //   filter[meetingBucket] = low | mid | high   → "Görüşme" select
    //   filter[scoreTrend]    = up | down          → "Net Artışı" select
    //   sortField + sortOrder = score | scoreTrend | meetings → "Sıralama" select
    // The matching controls are marked in students.component.html; each also needs its own form
    // control here and its labels in students.* of assets/i18n/tr.json.

    return `?${parts.join('&')}`;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
